import React, { useState } from 'react';
import { ChevronDown } from 'lucide-react';
import type { RuntimePolicySurface } from '../../generated/api/types.gen.js';
import { useQuery } from '@tanstack/react-query';
import { runtimePolicyOptionsQuery } from '../../services/api/runtime-policy.queries.js';
import { RuntimePolicyPicker } from './RuntimePolicyPicker.js';

interface RuntimePolicyMenuProps {
  surface: RuntimePolicySurface;
  /** Selected policy id; null shows the surface default. */
  value: number | null;
  onChange: (policyId: number) => void;
  disabled?: boolean;
  error?: string | null;
  /** Open the menu upwards (for controls at the bottom of the screen). */
  placement?: 'above' | 'below';
}

/** Compact policy control: a button naming the current policy that opens the full picker. */
export const RuntimePolicyMenu: React.FC<RuntimePolicyMenuProps> = ({
  surface,
  value,
  onChange,
  disabled = false,
  error = null,
  placement = 'below',
}) => {
  const [open, setOpen] = useState(false);
  const { data: options, isError } = useQuery(runtimePolicyOptionsQuery(surface));
  const current = options?.find((o) => (value === null ? o.isDefault : o.id === value));
  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-label="Runtime policy"
        className="inline-flex items-center gap-1.5 text-xs pl-2.5 pr-2 py-1.5 bg-surface-alt border rounded-lg text-body hover:bg-surface-hover"
      >
        <span className="text-faint">policy</span>
        <span className={isError ? 'text-danger' : 'text-body'}>
          {isError ? 'unavailable' : (current?.name ?? '…')}
        </span>
        <ChevronDown size={12} className="text-faint" />
      </button>
      {open && (
        <div
          className={`absolute left-0 z-20 w-80 rounded-lg border bg-surface p-2 shadow-lg ${
            placement === 'above' ? 'bottom-full mb-2' : 'top-full mt-2'
          }`}
        >
          <RuntimePolicyPicker
            surface={surface}
            value={value}
            disabled={disabled}
            onChange={(id) => {
              onChange(id);
              setOpen(false);
            }}
          />
          {error && <p className="mt-1 text-xs text-danger">{error}</p>}
        </div>
      )}
    </div>
  );
};
